import { CSSProperties } from "react";
import {
  TbDashboard,
  TbUserCircle,
  TbCar,
  TbInfoSquareRounded,
  TbCarCrash,
  TbCertificate,
  TbLicense,
  TbSteeringWheel,
  TbMap2,
  TbStars,
  TbHelp,
  TbSettings,
  TbLogout,
  TbTrash,
  TbArchive,
  TbArchiveOff,
  TbPencil,
  TbCards,
  TbShieldCheck,
  TbEye,
  TbFileStack,
  TbBell,
  TbChevronRight,
  TbSunHigh,
  TbSnowflake,
  TbPlus,
  TbChevronDown,
  TbChevronUp,
  TbCheck,
  TbPhoto,
  TbCertificate2,
  TbX,
  TbSearch,
  TbReload,
  TbBellRinging2,
  TbDots,
  TbDotsVertical,
  TbMailOpened,
  TbMail,
  TbTextWrapDisabled,
  TbLayout,
  TbLockSquareRounded,
  TbBuildingSkyscraper,
  TbTextWrap,
  TbCoinEuro,
  TbLogin,
  TbToggleRight,
  TbToggleLeft,
  TbArrowLeft,
  TbHourglassLow,
  TbBuildingWarehouse,
  TbPaperclip,
  TbSquare,
  TbArrowsLeftRight,
  TbSquareCheck,
  TbBuildingStore,
  TbExternalLink,
  TbServicemark,
  TbBroadcast,
  TbTrendingUp,
  TbGrid3X3,
  TbTag,
  TbMenu2,
  TbHistory,
  TbMapPin,
  TbPhone,
  TbCircleCheck,
  TbCircleX,
  TbInfoCircle,
  TbWorld,
} from "react-icons/tb";

type Props = {
  name: string;
  width?: number | string;
  height?: number | string;
  animation?: number | string;
  color?: any;
  style?: CSSProperties;
  onClick?: () => void;
};

// Aligner ces icons par ordres alphabétiques
export const Icon: React.FC<Props> = ({
  name,
  width,
  height,
  color,
  animation,
  style,
  onClick,
}) => {
  switch (name) {
    case "arrowLeft":
      return (
        <TbArrowLeft
          style={{ width, height, color, ...style }}
          onClick={onClick}
        />
      );

    case "bell":
      return <TbBell style={{ width, height, color }} />;
    case "bell2":
      return <TbBellRinging2 style={{ width, height, color }} />;
    case "buildingSkyscraper":
      return <TbBuildingSkyscraper style={{ width, height, color }} />;
    case "buildingWarehouse":
      return <TbBuildingWarehouse style={{ width, height, color }} />;
    case "link":
      return <TbExternalLink style={{ width, height, color }}  onClick={onClick}/>;
    case "cards":
      return <TbCards style={{ width, height }} />;
    case "broadcast":
      return <TbBroadcast style={{ width, height }} />;
    case "certificate":
      return <TbCertificate style={{ width, height }} />;
    case "certificate2":
      return <TbCertificate2 style={{ width, height }} />;
    case "chevronRight":
      return <TbChevronRight style={{ width, height }} />;
    case "chevronDown":
      return <TbChevronDown style={{ width, height }} />;
    case "chevronUp":
      return <TbChevronUp style={{ width, height }} />;
    case "trending-up":
      return <TbTrendingUp style={{ width, height, color }} />;

    /*
     * ── Icons the Pub screen asked for and never got ──────────────────────
     *
     * `default: return null` below means an unknown name renders NOTHING, in
     * silence. The Pub management screen referenced ten names that were never
     * in this switch - map-pin, phone, check-circle, x-circle, x, star, edit,
     * info, image, trash-2 - so its Status chips, its clear-filters button, its
     * gallery delete button and every section header in the add/edit modal drew
     * an empty space. Nothing failed; the icons were simply absent, which is
     * why it survived review.
     *
     * Two groups below: genuinely new glyphs, then aliases for names that
     * already exist here under a different spelling. The aliases are worth
     * keeping rather than "fixing at the call site": the tabler/feather
     * spellings (`x`, `trash-2`, `edit`) are what anyone writing new UI will
     * reach for, and a lookup that quietly returns nothing is a trap.
     */
    case "map-pin":
      return <TbMapPin style={{ width, height, color, ...style }} />;
    case "phone":
      return <TbPhone style={{ width, height, color, ...style }} />;
    case "check-circle":
      return <TbCircleCheck style={{ width, height, color, ...style }} />;
    case "x-circle":
      return <TbCircleX style={{ width, height, color, ...style }} />;
    case "info":
      return <TbInfoCircle style={{ width, height, color, ...style }} />;
    /*
     * Diffusion sans ciblage géographique (colonne « Diffusion » de l'écran
     * Pub). `broadcast` existe déjà plus haut mais n'accepte pas `color`, et
     * cette pastille est rendue en blanc sur fond coloré : la réutiliser aurait
     * donné un glyphe noir invisible sur le chip.
     */
    case "globe":
      return <TbWorld style={{ width, height, color, ...style }} />;

    // Aliases onto glyphs already defined further down this switch.
    case "x":
      return (
        <TbX style={{ width, height, color, ...style }} onClick={onClick} />
      );
    case "star":
      return <TbStars style={{ width, height, color, ...style }} />;
    case "edit":
      return <TbPencil style={{ width, height, color, ...style }} />;
    case "image":
      return <TbPhoto style={{ width, height, color, ...style }} />;
    case "trash-2":
      return (
        <TbTrash style={{ width, height, color, ...style }} onClick={onClick} />
      );
    case "grid":
      return <TbGrid3X3 style={{ width, height, color }} />;
    // Journaux (audit trail + system logs) sidebar entry.
    case "history":
      return <TbHistory style={{ width, height, color, ...style }} />;
    case "tag":
      return <TbTag style={{ width, height, color }} />;
    case "check":
      return (
        <TbCheck style={{ width, height, color, ...style }} onClick={onClick} />
      );
    case "close":
      return <TbX style={{ width, height, color }} />;
    case "servicemark":
      return <TbServicemark style={{ width, height, color, ...style }} />;
    case "buildingStore":
      return <TbBuildingStore style={{ width, height, color, ...style }} />;
    case "car":
      return <TbCar style={{ width, height, color }} />;
    case "coinEuro":
      return <TbCoinEuro style={{ width, height, color }} />;

    case "dashboard":
      return <TbDashboard style={{ width, height }} />;
    case "dots":
      return <TbDots style={{ width, height }} />;
    case "dotsVertical":
      return (
        <TbDotsVertical
          style={{ width, height, color, ...style }}
          onClick={onClick}
        />
      );

    case "eye":
      return (
        <TbEye style={{ width, height, color, ...style }} onClick={onClick} />
      );

    case "fileStack":
      return <TbFileStack style={{ width, height }} />;

    case "help":
      return <TbHelp style={{ width, height, color, ...style }} />;

    case "hourglassLow":
      return <TbHourglassLow style={{ width, height, color, ...style }} />;

    case "login":
      return (
        <TbLogin
          style={{ width, height, color, animation, ...style }}
          onClick={onClick}
        />
      );
    case "layout":
      return <TbLayout style={{ width, height }} />;
    case "license":
      return <TbLicense style={{ width, height, color }} />;
    case "logout":
      return <TbLogout style={{ width, height }} />;
    case "lockSquareRounded":
      return <TbLockSquareRounded style={{ width, height }} />;
    case "arrowLeftRight":
      return <TbArrowsLeftRight style={{ width, height }} />;

    case "menu":
      return (
        <TbMenu2
          style={{ width, height, color, ...style }}
          onClick={onClick}
        />
      );
    case "map2":
      return <TbMap2 style={{ width, height }} />;
    case "mailOpened":
      return <TbMailOpened style={{ width, height, color }} />;
    case "mail":
      return <TbMail style={{ width, height, color }} />;

    case "notification":
      return <TbInfoSquareRounded style={{ width, height }} />;

    case "pencil":
      return (
        <TbPencil
          style={{ width, height, color, ...style }}
          onClick={onClick}
        />
      );
    case "plus":
      return <TbPlus style={{ width, height, color }} />;
    case "photo":
      return <TbPhoto style={{ width, height, color }} />;
    case "paperClip":
      return (
        <TbPaperclip
          style={{ width, height, color, ...style }}
          onClick={onClick}
        />
      );

    case "reload":
      return (
        <TbReload
          style={{ width, height, color, animation, ...style }}
          onClick={onClick}
        />
      );

    case "sinistre":
      return <TbCarCrash style={{ width, height }} />;
    case "stars":
      return <TbStars style={{ width, height }} />;
    case "steeringWheel":
      return <TbSteeringWheel style={{ width, height }} />;
    case "search":
      return <TbSearch style={{ width, height, color }} />;
    case "settings":
      return <TbSettings style={{ width, height }} />;
    case "shieldCheck":
      return <TbShieldCheck style={{ width, height }} />;
    case "sunHigh":
      return <TbSunHigh style={{ width, height }} />;
    case "snowFlake":
      return <TbSnowflake style={{ width, height }} />;
    case "square":
      return (
        <TbSquare
          style={{ width, height, color, ...style }}
          onClick={onClick}
        />
      );
    case "squareCheck":
      return (
        <TbSquareCheck
          style={{ width, height, color, ...style }}
          onClick={onClick}
        />
      );

    case "userCircle":
      return <TbUserCircle style={{ width, height, color }} />;

    case "toggleRight":
      return (
        <TbToggleRight
          style={{ width, height, color, ...style }}
          onClick={onClick}
        />
      );
    case "toggleLeft":
      return (
        <TbToggleLeft
          style={{ width, height, color, ...style }}
          onClick={onClick}
        />
      );
    case "trash":
      return (
        <TbTrash style={{ width, height, color, ...style }} onClick={onClick} />
      );
    case "archive":
      return (
        <TbArchive
          style={{ width, height, color, ...style }}
          onClick={onClick}
        />
      );
    case "archiveOff":
      return (
        <TbArchiveOff
          style={{ width, height, color, ...style }}
          onClick={onClick}
        />
      );
    case "textWrapDisabled":
      return <TbTextWrapDisabled style={{ width, height, color }} />;
    case "textWrap":
      return <TbTextWrap style={{ width, height, color }} />;

    default:
      /*
       * An unknown name is a BUG, not a valid "no icon" request.
       *
       * Returning null silently is how ten missing icons on the Pub screen went
       * unnoticed: the layout still worked, the space was just empty. In
       * development this now says so, loudly enough to be seen in the console
       * during the change that introduced it.
       *
       * Production still renders nothing rather than throwing - a missing glyph
       * must never take down a screen - so the behaviour customers see is
       * unchanged.
       */
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(
          `[Icon] unknown icon name "${name}" - nothing will be rendered. ` +
            `Add a case for it in components/Icon/Icon.tsx.`
        );
      }
      return null;
  }
};
